import type { DeliveryGroupDraft, DeliveryGroupStopDraft } from './deliveryGroupTypes';
import { resolveGroupApplicationIds } from './deliveryGroupModel';
import { filterCargoForGroup } from './groupScopeHelpers';
import {
  filterCargoForLoadingExecution,
  listRealLoadingPointsForGroup,
  type ExecutionValidationIssue
} from './executionBindingHelpers';
import type { ExecutionTopologyPreview } from './executionTopologyHelpers';
import type {
  AllocationEdgeDraft,
  FromStopDraftByUid,
  QuantityDraftByCargo,
  ToStopDraftByUid
} from './executionDraftTypes';
import { validateExecutionGraph } from './validateExecutionGraph';

/** Per-group active loading stop (stopUid), not route-wide position. */
export type ActiveLoadingStopByGroup = Record<string, string>;

export type LoadingExecutionContext = {
  groupUid: string;
  stopUid: string;
  position: number;
};

export type LoadingOwnershipIndex = {
  groupByLoadingStopUid: Map<string, string>;
  stopUidToGroupUid: Map<string, string>;
  stopUidToPosition: Map<string, number>;
  groupByStopUid: Map<string, DeliveryGroupDraft>;
};

export function buildLoadingOwnershipIndex(
  groups: DeliveryGroupDraft[],
  topology?: ExecutionTopologyPreview | null
): LoadingOwnershipIndex {
  const groupByLoadingStopUid = new Map<string, string>();
  const stopUidToGroupUid = new Map<string, string>();
  const stopUidToPosition = new Map<string, number>();
  const groupByStopUid = new Map<string, DeliveryGroupDraft>();

  for (const group of groups) {
    for (const point of listRealLoadingPointsForGroup(group, topology)) {
      groupByLoadingStopUid.set(point.stopUid, group.uid);
      stopUidToGroupUid.set(point.stopUid, group.uid);
      stopUidToPosition.set(point.stopUid, point.position);
      groupByStopUid.set(point.stopUid, group);
    }
  }

  return { groupByLoadingStopUid, stopUidToGroupUid, stopUidToPosition, groupByStopUid };
}

export function resolveGroupLoadingContext(
  group: DeliveryGroupDraft,
  activeLoadingStopByGroup: ActiveLoadingStopByGroup,
  topology?: ExecutionTopologyPreview | null
): LoadingExecutionContext | null {
  const points = listRealLoadingPointsForGroup(group, topology);
  if (points.length === 0) return null;

  const savedUid = activeLoadingStopByGroup[group.uid];
  const matched = savedUid ? points.find((p) => p.stopUid === savedUid) : undefined;
  const point = matched ?? points[0];

  return {
    groupUid: group.uid,
    stopUid: point.stopUid,
    position: point.position
  };
}

export function resolveActiveLoadingStopByUid(
  group: DeliveryGroupDraft,
  stopUid: string
): DeliveryGroupStopDraft | null {
  return group.loadingStops.find((s) => s.uid === stopUid) ?? null;
}

export function syncActiveLoadingStopByGroup(
  groups: DeliveryGroupDraft[],
  prev: ActiveLoadingStopByGroup,
  topology?: ExecutionTopologyPreview | null
): ActiveLoadingStopByGroup {
  const next: ActiveLoadingStopByGroup = {};
  let changed = false;

  for (const group of groups) {
    const points = listRealLoadingPointsForGroup(group, topology);
    if (points.length === 0) continue;

    const savedUid = prev[group.uid];
    const stillValid = savedUid && points.some((p) => p.stopUid === savedUid);
    const stopUid = stillValid ? savedUid! : points[0].stopUid;
    next[group.uid] = stopUid;
    if (prev[group.uid] !== stopUid) changed = true;
  }

  if (Object.keys(prev).length !== Object.keys(next).length) changed = true;

  return changed ? next : prev;
}

export function filterCargoForGroupExecutionContext(params: {
  cargoRows: any[];
  group: DeliveryGroupDraft;
  context: LoadingExecutionContext | null;
  fromStopDraft: FromStopDraftByUid;
  quantityDraft: QuantityDraftByCargo;
  allocationEdges?: AllocationEdgeDraft[];
}): any[] {
  const { cargoRows, group, context, fromStopDraft, quantityDraft, allocationEdges } = params;
  if (!context) return [];

  const inGroup = filterCargoForGroup(cargoRows, group);
  return filterCargoForLoadingExecution({
    cargoRows: inGroup,
    activeLoadingStopUid: context.stopUid,
    fromStopDraft,
    quantityDraft,
    allocationEdges
  });
}

/** Checkbox state scoped to current group + loading stopUid. */
export function isCargoCheckedInExecutionContext(
  cargoId: number,
  fromStopDraft: FromStopDraftByUid,
  quantityDraft: QuantityDraftByCargo,
  context: LoadingExecutionContext
): boolean {
  const qty = Number(quantityDraft[cargoId] ?? 0);
  if (qty <= 0) return false;
  return fromStopDraft[cargoId] === context.stopUid;
}

export function isCargoCheckedWithAllocationEdges(
  cargoId: number,
  edges: AllocationEdgeDraft[],
  context: LoadingExecutionContext
): boolean {
  return edges.some(
    (e) =>
      Number(e.cargoId) === Number(cargoId) &&
      e.fromStopUid === context.stopUid &&
      e.quantity > 0
  );
}

export function buildActiveLoadingStopByGroupFromAllocations(params: {
  groups: DeliveryGroupDraft[];
  fromStopDraft: FromStopDraftByUid;
  allocationEdges?: AllocationEdgeDraft[];
  preferredGroupUid?: string | null;
}): ActiveLoadingStopByGroup {
  const result: ActiveLoadingStopByGroup = {};

  for (const group of params.groups) {
    const points = listRealLoadingPointsForGroup(group);
    if (points.length === 0) continue;

    const uidsUsed = new Set<string>();
    if (params.allocationEdges?.length) {
      for (const edge of params.allocationEdges) {
        if (edge.quantity > 0 && edge.fromStopUid && points.some((p) => p.stopUid === edge.fromStopUid)) {
          uidsUsed.add(edge.fromStopUid);
        }
      }
    }
    for (const fromUid of Object.values(params.fromStopDraft)) {
      if (fromUid && points.some((p) => p.stopUid === fromUid)) {
        uidsUsed.add(fromUid);
      }
    }

    const preferred =
      params.preferredGroupUid === group.uid
        ? points.find((p) => uidsUsed.has(p.stopUid))
        : undefined;
    const fromAlloc = points.find((p) => uidsUsed.has(p.stopUid));
    const point = preferred ?? fromAlloc ?? points[0];
    result[group.uid] = point.stopUid;
  }

  return result;
}

export function validateStep4ExecutionBindings(params: {
  groups: DeliveryGroupDraft[];
  selectedIds: number[];
  fromStopDraft: FromStopDraftByUid;
  toStopDraft: ToStopDraftByUid;
  quantityDraft: QuantityDraftByCargo;
  allocationEdges?: AllocationEdgeDraft[];
  cargoApplicationById?: Record<number, number | undefined>;
  cargoAvailableById?: Record<number, number | undefined>;
  cargoCapById?: Record<number, number | undefined>;
  topology?: ExecutionTopologyPreview | null;
}): ExecutionValidationIssue[] {
  const graphIssues = validateExecutionGraph({
    groups: params.groups,
    selectedIds: params.selectedIds,
    fromStopDraft: params.fromStopDraft,
    toStopDraft: params.toStopDraft,
    quantityDraft: params.quantityDraft,
    allocationEdges: params.allocationEdges,
    topology: params.topology,
    cargoApplicationById: params.cargoApplicationById,
    cargoAvailableById: params.cargoAvailableById,
    cargoCapById: params.cargoCapById
  });

  return graphIssues.map((issue) => ({
    code:
      issue.code === 'MISSING_LOADING_STOP'
        ? 'MISSING_LOADING_STOP'
        : issue.code === 'MISSING_UNLOADING_STOP'
          ? 'MISSING_UNLOADING_STOP'
          : issue.code === 'CROSS_GROUP'
            ? 'CROSS_GROUP'
            : issue.code === 'QTY_EXCEEDS_AVAILABLE'
              ? 'QTY_EXCEEDS_AVAILABLE'
              : 'MISSING_LOADING_STOP',
    message: issue.message
  }));
}

/** @deprecated use groupByLoadingStopUid — position index kept for structural compat only */
export function buildLegacyPositionOwnershipIndex(
  groups: DeliveryGroupDraft[],
  topology?: ExecutionTopologyPreview | null
): Map<number, string> {
  const index = buildLoadingOwnershipIndex(groups, topology);
  const byPos = new Map<number, string>();
  for (const [stopUid, pos] of Array.from(index.stopUidToPosition.entries())) {
    const groupUid = index.stopUidToGroupUid.get(stopUid);
    if (groupUid) byPos.set(pos, groupUid);
  }
  return byPos;
}

export { resolveGroupApplicationIds };
